import { useMemo } from 'react'
import {
  Currency,
  CurrencyTradingLimitDetail,
  Market,
  TradingControlLimitUserUtilisation,
  TradingControlLimitUserUtilisationCurrency,
  TradingLimitType,
  User
} from 'api'
import { filteredColumnDefsBuilder } from 'view/components/datagrid'

import { ColDef, ColGroupDef } from 'ag-grid-community'
import { tradingLimitsColumnDefs, TradingLimitsGridColumnId } from './TradingLimitsGrid/TradingLimitsGridModel'
import { DateLike, toLocalDateString } from 'utils'
import { useSelector } from 'react-redux'
import { platformDetailSelector } from 'selectors/platformSelectors'

export const useFilteredTradingLimitsColumnDefs = (tradingLimitType: TradingLimitType): (ColGroupDef<any> | ColDef<any, any>)[] =>
  useMemo(() => {
    return filteredColumnDefsBuilder(tradingLimitsColumnDefs)
      .withoutIf(tradingLimitType === TradingLimitType.NET, [
        TradingLimitsGridColumnId.TRADING_LIMITS_GROSS_AVAILABLE,
        TradingLimitsGridColumnId.TRADING_LIMITS_GROSS_UTILISATION
      ])
      .withoutIf(tradingLimitType === TradingLimitType.GROSS, [
        TradingLimitsGridColumnId.TRADING_LIMITS_NET_AVAILABLE_BUY,
        TradingLimitsGridColumnId.TRADING_LIMITS_NET_AVAILABLE_SELL,
        TradingLimitsGridColumnId.TRADING_LIMITS_NET_UTILISATION
      ])
      .withoutIf(tradingLimitType === TradingLimitType.NONE, [
        TradingLimitsGridColumnId.TRADING_LIMITS_GROSS_AVAILABLE,
        TradingLimitsGridColumnId.TRADING_LIMITS_GROSS_UTILISATION,
        TradingLimitsGridColumnId.TRADING_LIMITS_NET_AVAILABLE_BUY,
        TradingLimitsGridColumnId.TRADING_LIMITS_NET_AVAILABLE_SELL,
        TradingLimitsGridColumnId.TRADING_LIMITS_NET_UTILISATION
      ])
      .build()
  }, [tradingLimitType])

const defaultTradingLimits = {
  market: Market.EUROPE,
  utilisationDate: new Date(),
  utilisationGross: 0,
  availableGross: 0,
  utilisationNet: 0,
  utilisationNetBuy: 0,
  utilisationNetSell: 0,
  availableNetBuy: 0,
  availableNetSell: 0
}

export const useGetTradingLimitsRows = (
  user: User | null,
  tradingControlLimits: TradingControlLimitUserUtilisation | null
): CurrencyTradingLimitDetail[] => {
  const platformDetail = useSelector(platformDetailSelector)
  return useMemo(() => {
    if (!user) return []
    const utilisationByCurrency: Array<CurrencyTradingLimitDetail> = Object.values(Currency)
      .map(currency => {
        const limit = user.tradingControlLimit.limitsByCurrency[currency]?.limit || 0

        const tradingControl = tradingControlLimits?.utilisationByCurrency[currency] ?? defaultTradingLimits

        // Values will be reset if trade dates differ
        const marketTradeDateEU = platformDetail?.platform.marketDetails[Market.EUROPE]?.tradeDate
        const marketTradeDateUS = platformDetail?.platform.marketDetails[Market.US]?.tradeDate

        const resetUtilisationValues = isTradeDateDifferent(marketTradeDateEU, marketTradeDateUS, tradingControl)

        const utilisationGross = resetUtilisationValues ? 0 : tradingControl.utilisationGross
        const utilisationNet = resetUtilisationValues ? 0 : tradingControl.utilisationNet || 0
        const utilisationNetBuy = resetUtilisationValues ? 0 : tradingControl.utilisationNetBuy
        const utilisationNetSell = resetUtilisationValues ? 0 : tradingControl.utilisationNetSell

        const isUtilizationNetOverLimit = Math.abs(utilisationNet) > limit
        const limitAfterUtilisationGross = limit - utilisationGross
        const limitAfterUtilisationNetBuy = limit - utilisationNet
        const limitAfterUtilisationNetSell = -limit - utilisationNet === 0 ? 0 : -limit - utilisationNet
        const result = {
          currency,
          limit,
          ...(limit === 0
            ? defaultTradingLimits
            : {
                market: tradingControl.market,
                utilisationDate: tradingControl.utilisationDate,
                utilisationGross,
                availableGross: limitAfterUtilisationGross > 0 ? limitAfterUtilisationGross : 0,
                utilisationNet,
                availableNetBuy: isUtilizationNetOverLimit && utilisationNet >= 0 ? 0 : limitAfterUtilisationNetBuy,
                availableNetSell: isUtilizationNetOverLimit && utilisationNet <= 0 ? 0 : limitAfterUtilisationNetSell,
                utilisationNetBuy,
                utilisationNetSell
              })
        }

        return result
      })
      .sort((a, b) => (a.currency > b.currency ? 1 : -1))

    return utilisationByCurrency
  }, [platformDetail?.platform.marketDetails, tradingControlLimits?.utilisationByCurrency, user])
}

const isTradeDateDifferent = (
  marketTradeDateEU: DateLike | undefined,
  marketTradeDateUS: DateLike | undefined,
  tradingControl: TradingControlLimitUserUtilisationCurrency
): Boolean => {
  if (tradingControl.market === Market.EUROPE) {
    return marketTradeDateEU !== toLocalDateString(tradingControl.utilisationDate)
  } else {
    return marketTradeDateUS !== toLocalDateString(tradingControl.utilisationDate)
  }
}
